import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { RequestParticipant } from "models/interfaces";
import { normalizeOrders } from "utils/signerOrderUtils";
import { updateSignerOrders } from "redux/appSlice";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import { getRecipientsLevelColor } from "../ApproversAndSigners.tokens";
import { CaretDownGlyph, CaretUpGlyph, PlusGlyph } from "./RecipientsIcons";

const styles = approversAndSignersStyles;

interface SignerLevelHeaderProps {
  /** 0-based position of this level in the ordered list. */
  levelIndex: number;
  /** Total number of levels, used to dim the edge arrows. */
  levelCount: number;
  /** The signers' shared `order` value. */
  order: number;
  signers: RequestParticipant[];
  dispatch: any;
  /** Adds a fresh signer straight into this level. */
  onAddToLevel: (order: number) => void;
}

/**
 * "1 ▸ Level 1 — notified first ———— ⌃⌄ + Add signer here" strip above each
 * signing level, per the design's `g.showHeader` block.
 */
const SignerLevelHeader = ({
  levelIndex,
  levelCount,
  order,
  signers,
  dispatch,
  onAddToLevel,
}: SignerLevelHeaderProps) => {
  const canMoveUp = levelIndex > 0;
  const canMoveDown = levelIndex < levelCount - 1;

  /** Swaps this whole level with the one before / after it. */
  const moveLevel = (direction: "up" | "down") => {
    const orders = Array.from(
      new Set(signers.map((signer) => Number(signer.order))),
    ).sort((a, b) => a - b);
    const from = orders.indexOf(order);
    const to = direction === "up" ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= orders.length) return;

    const targetOrder = orders[to];
    const swapped = signers.map((signer) => {
      const signerOrder = Number(signer.order);
      if (signerOrder === order) return { ...signer, order: targetOrder };
      if (signerOrder === targetOrder) return { ...signer, order };
      return signer;
    });

    dispatch(updateSignerOrders(normalizeOrders(swapped)));
  };

  return (
    <Box sx={styles.levelHeader}>
      <Box sx={styles.levelNum(getRecipientsLevelColor(levelIndex))}>
        {levelIndex + 1}
      </Box>
      <Box component="span" sx={styles.levelLabel}>
        Level {levelIndex + 1}
      </Box>
      <Box component="span" sx={styles.levelSub}>
        {levelIndex === 0
          ? "— notified first"
          : `— after Level ${levelIndex} completes`}
      </Box>
      <Box sx={styles.levelRule} />

      <Box sx={styles.levelMoveGroup}>
        <Tooltip title="Move whole level earlier" arrow>
          <IconButton
            disableRipple
            aria-label="Move whole level earlier"
            disabled={!canMoveUp}
            onClick={() => moveLevel("up")}
            sx={styles.levelMove(!canMoveUp)}
          >
            <CaretUpGlyph sx={styles.levelMoveIcon} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Move whole level later" arrow>
          <IconButton
            disableRipple
            aria-label="Move whole level later"
            disabled={!canMoveDown}
            onClick={() => moveLevel("down")}
            sx={styles.levelMove(!canMoveDown)}
          >
            <CaretDownGlyph sx={styles.levelMoveIcon} />
          </IconButton>
        </Tooltip>
      </Box>

      <Button
        disableRipple
        aria-label={`Add signer to Level ${levelIndex + 1}`}
        onClick={() => onAddToLevel(order)}
        sx={styles.levelAdd}
      >
        <PlusGlyph sx={styles.levelAddIcon} />
        Add signer here
      </Button>
    </Box>
  );
};

export default SignerLevelHeader;
